import * as Sentry from '@sentry/nextjs';
import { CaptureContext,Extras } from '@sentry/types';
import { AxiosResponse } from 'axios';

type SentryError = {
  message: string;
  response: AxiosResponse;
}

export const isSentryError = (error: unknown): error is SentryError =>
  !!(error as SentryError)?.message && !!(error as SentryError)?.response;

class SentryService {

  private async withRetryAfter(action: () => Promise<void>): Promise<void> {
    let shouldRetry = true;

    while (shouldRetry) {
      try {
        await action();
        shouldRetry = false; // Success, exit the loop
      } catch (error: unknown) {

        if (isSentryError(error) && error.message.includes('429')) {
          const retryAfterHeader = error?.response?.headers?.['retry-after'];
          if (retryAfterHeader) {
            const waitTime = parseInt(retryAfterHeader) * 1000;
            await new Promise(resolve => setTimeout(resolve, waitTime));
            // Continue the loop for retry
          } else {
            shouldRetry = false; // No retry-after header, exit the loop
          }
        } else {
          shouldRetry = false; // Not a 429 error, exit the loop
        }
      }
    }
  }

  async captureExceptionWithRetryAfter(exception: unknown, extra?: Extras): Promise<void> {
    await this.withRetryAfter(async () => {
      Sentry.captureException(exception, { extra });
    });
  }

  async captureMessageWithRetryAfter(message: string, options?: CaptureContext): Promise<void> {
    await this.withRetryAfter(async () => {
      await new Promise<void>((resolve) => {
        Sentry.captureMessage(message, options);
        resolve();
      });
    });
  }

}

export default new SentryService();
